import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import Keycloak from '@inoventoryapps/keycloak-js';
import { Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SERVICE_URL } from '../../constants/url.constants';
import { KeyService } from '../../services/key/key.service';

interface EncryptedPayload {
    encrypted: string;
    iv: string;
}

interface IKeycloakCrypto {
    encrypt: (key: { publicKey: CryptoKey }, input: unknown, useHKDF: boolean) => Promise<EncryptedPayload>;
    decrypt: (key: { publicKey: CryptoKey }, input: string, iv: string | null, useHKDF: boolean) => Promise<unknown>;
    dpopProof: (url: string, method: string, claims: string, kid: string | null) => Promise<string>;
}

const excludedRoutes = new Set([SERVICE_URL.API_SETU, 'signer', 'api/crypto/jwks', environment.imageBaseUrl]);
const excludedDPoPRoutes = new Set(['signer', 'api/crypto/jwks', SERVICE_URL.API_SETU]);

/**
 * Same-origin static assets (e.g. /i18n/en.json, /images/*) are served by this app
 * itself, so they need neither encryption nor a DPoP proof. Every API call in the app
 * is built from an absolute `environment.*BaseUrl`, so a non-absolute URL is always a
 * local asset.
 */
function isLocalAsset(url: string): boolean {
    return !/^https?:\/\//i.test(url);
}

function shouldEncrypt(req: HttpRequest<unknown>): boolean {
    if (!environment.enableEncryption) {
        return false;
    }
    if (req.body instanceof Blob || req.body instanceof FormData || req.responseType === 'blob') {
        return false;
    }
    return !Array.from(excludedRoutes).some((route) => route && req.url.includes(route));
}

export const encryptionInterceptor: HttpInterceptorFn = (req, next) => {
    if (isLocalAsset(req.url)) {
        return next(req);
    }

    const keyService = inject(KeyService);
    const keycloak = inject(Keycloak) as unknown as IKeycloakCrypto;
    const keycloakApiBaseUrl = environment.adminLokOSBaseUrl;
    const fdmApiBaseUrl = environment.fdmApiBaseUrl;

    // src/environments is gitignored and supplied per-environment, so these base URLs
    // may or may not carry a trailing slash. Plain concatenation produced
    // `https://api.lokos.inapi/crypto/jwks` in production builds, which failed every
    // request; normalise instead of relying on the value's shape.
    const jwksUrl = (): string => {
        const baseUrl = req.url.includes(SERVICE_URL.KEY_CLOAK) ? keycloakApiBaseUrl : fdmApiBaseUrl;
        return `${baseUrl.replace(/\/+$/, '')}/api/crypto/jwks`;
    };

    return new Observable<HttpEvent<unknown>>((observer) => {
        keyService
            .getClientKey()
            .then(async () => {
                try {
                    const needsEncrypt = shouldEncrypt(req);
                    let serverPublicKey: CryptoKey | undefined;
                    let encryptedPayload: EncryptedPayload | undefined;
                    if (needsEncrypt) {
                        serverPublicKey = await keyService.getServerPublicKey(jwksUrl(), req.url);
                        encryptedPayload = await keycloak.encrypt({ publicKey: serverPublicKey }, req.body, false);
                    } else if (!req.url.includes('signer')) {
                        // This request does not need an encrypted body — the key is only
                        // used to attach a DPoP proof and to decrypt the response. Fail
                        // open so an unreachable JWKS host cannot break requests that
                        // never needed encryption in the first place.
                        try {
                            serverPublicKey = await keyService.getServerPublicKey(jwksUrl(), req.url);
                        } catch (error) {
                            console.warn('Unable to fetch the server public key; continuing without DPoP', error);
                        }
                    }

                    const setHeaders: Record<string, string> = {
                        'x-encrypted': 'true',
                    };
                    if (!Array.from(excludedDPoPRoutes).some((route) => req.url.includes(route)) && serverPublicKey) {
                        setHeaders['DPoP'] = await keycloak.dpopProof(
                            req.url,
                            req.method,
                            encryptedPayload?.iv ?? '',
                            keyService.getKid(req.url),
                        );
                    }

                    const newReq = req.clone({
                        body: needsEncrypt && encryptedPayload ? { data: encryptedPayload.encrypted } : req.body,
                        setHeaders,
                    });

                    const decryptBody = async (
                        iv: string | null,
                        body: Record<string, string> | null,
                    ): Promise<Record<string, unknown>> => {
                        if (body?.['data'] && serverPublicKey) {
                            try {
                                return (await keycloak.decrypt(
                                    { publicKey: serverPublicKey },
                                    body['data'],
                                    iv,
                                    false,
                                )) as Record<string, unknown>;
                            } catch {
                                return body;
                            }
                        }
                        return {};
                    };

                    next(newReq)
                        .pipe(
                            switchMap(async (event: HttpEvent<unknown>) => {
                                if (event instanceof HttpResponse && event.headers.get('x-encrypted') === 'true') {
                                    const body = event.body as Record<string, string> | null;
                                    if (body?.['data'] && serverPublicKey) {
                                        const decodedBody = await keycloak.decrypt(
                                            { publicKey: serverPublicKey },
                                            body['data'],
                                            event.headers.get('x-crypto-enabled'),
                                            false,
                                        );
                                        observer.next(event.clone({ body: decodedBody }));
                                    } else {
                                        observer.next(event.clone({ body: {} }));
                                    }
                                } else if (event instanceof HttpResponse) {
                                    observer.next(event);
                                } else {
                                    observer.next(event);
                                }
                            }),
                            catchError(async (error) => {
                                if (error instanceof HttpErrorResponse && error.headers.get('x-encrypted') === 'true') {
                                    const decryptedBody = await decryptBody(
                                        error.headers.get('x-crypto-enabled'),
                                        error.error,
                                    );
                                    observer.error(
                                        new HttpErrorResponse({
                                            error: decryptedBody,
                                            headers: error.headers,
                                            status: error.status,
                                            statusText: error.statusText,
                                            url: error.url ?? undefined,
                                            redirected: error.redirected,
                                            responseType: error.responseType,
                                        }),
                                    );
                                    return [];
                                }
                                observer.error(error);
                                return [];
                            }),
                        )
                        .subscribe({
                            complete: () => observer.complete(),
                        });
                } catch (error) {
                    observer.error(error);
                }
            })
            // Without this the observer is never notified when the client key cannot be
            // read, and the request hangs forever with the global loader showing.
            .catch((error) => observer.error(error));
    });
};
